// Steps 3–5 of My style: the built profile, three preview drafts, "use this style", and a box
// right under the previews to say what is off (P4-08). A rule change saves a new version and
// drafts fresh previews in it. Approving an older version is the rollback.

import { useState } from 'react';
import type { StylePreview, StyleProfile, StyleRule, StyleRulesRequest, StyleState } from '../../api/types';
import { ruleLine, situationLabel, STYLE_SITUATIONS } from '../../lib/styleIntake';

function RuleList({ title, rules, onRemove }: { title: string; rules: StyleRule[]; onRemove?: (index: number) => void }) {
  if (!rules.length) return null;
  return (
    <div className="stylerules">
      <h4>{title}</h4>
      <ul>
        {rules.map((rule, index) => (
          <li key={index}>
            {ruleLine(rule)}
            {onRemove ? (
              <button type="button" className="styleremove" aria-label={`Remove ${rule.rule}`} onClick={() => onRemove(index)}>×</button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FeedbackForm({ onAdd, busy }: { onAdd: (kind: 'always' | 'never', rule: StyleRule) => void; busy: boolean }) {
  const [kind, setKind] = useState<'always' | 'never'>('never');
  const [rule, setRule] = useState('');
  const [reason, setReason] = useState('');
  const ready = rule.trim() && reason.trim();
  return (
    <div className="stylefeedback">
      <h4>Something off? Tell Scout</h4>
      <div className="stylenewrule">
        <select value={kind} onChange={(event) => setKind(event.target.value as 'always' | 'never')} aria-label="Rule kind">
          <option value="never">Never</option>
          <option value="always">Always</option>
        </select>
        <input placeholder="e.g. Never open with a question" value={rule} onChange={(event) => setRule(event.target.value)} />
        <input placeholder="Why" value={reason} onChange={(event) => setReason(event.target.value)} />
        <button
          type="button"
          className="btn"
          disabled={busy || !ready}
          onClick={() => {
            onAdd(kind, { rule: rule.trim(), reason: reason.trim() });
            setRule('');
            setReason('');
          }}
        >
          {busy ? 'Redrafting…' : 'Save and redraft'}
        </button>
      </div>
    </div>
  );
}

function PreviewCard({ preview }: { preview: StylePreview }) {
  return (
    <div className="stylepreview">
      <p className="styleemailnum">{preview.contact_name ?? 'Contact'} at {preview.domain}</p>
      {preview.declined_reason ? (
        <p className="stylenote">Couldn't draft this one: {preview.declined_reason}</p>
      ) : (
        <>
          <p className="stylesubject">{preview.subject}</p>
          <pre className="stylebody">{preview.body}</pre>
          {preview.rule_note ? <p className="stylerulenote">{preview.rule_note}</p> : null}
        </>
      )}
      {preview.house_body ? (
        <details>
          <summary>The house-style draft it replaces</summary>
          <pre className="stylebody">{preview.house_body}</pre>
        </details>
      ) : null}
    </div>
  );
}

export function StyleProfilePanel({
  state,
  profile,
  busyStep,
  onPreview,
  onApprove,
  onEditRules,
}: {
  state: StyleState;
  profile: StyleProfile;
  busyStep: string | null;
  onPreview: () => void;
  onApprove: (version: number) => void;
  onEditRules: (changes: StyleRulesRequest) => void;
}) {
  const isLive = state.active_version === profile.version;
  const previews = state.previews.length ? state.previews : profile.previews;
  // The server refuses approval unless a preview has a draft; the button says so first.
  const anyPreviewPassed = previews.some((preview) => !preview.declined_reason);
  const previewing = busyStep === 'preview';
  const ruleCount = profile.always.length + profile.never.length + profile.cartesia_claims.length;

  // A rule edit replaces the whole list, so it sends the profile's list with one change.
  function removeRule(kind: 'always' | 'never', index: number) {
    onEditRules({ [kind]: profile[kind].filter((_, position) => position !== index) });
  }
  function addRule(kind: 'always' | 'never', rule: StyleRule) {
    onEditRules({ [kind]: [...profile[kind], rule] });
  }

  return (
    <section className="stylestep">
      <h3>
        Your style, version {profile.version}
        {isLive ? <span className="chip done stylelive">in use</span> : null}
      </h3>
      {profile.impersonator ? (
        <p className="stylenote">Made by {profile.impersonator} acting as you.</p>
      ) : null}
      <p>{profile.voice_summary}</p>
      <p className="stylehint">
        Greeting: {profile.greeting || 'Hi'} · Sign-off: {profile.sign_off || 'Best'} · Default ask:{' '}
        {profile.default_ask || 'not set'} · At most {profile.email_max_words} words
      </p>
      {profile.house_conflicts.length ? (
        <ul className="styleasks">
          {profile.house_conflicts.map((conflict) => <li key={conflict}>Not kept: {conflict}</li>)}
        </ul>
      ) : null}

      {/* The previews and the one main action for where the strategist is. */}
      {previews.length ? (
        <div className="stylepreviews">
          {previews.map((preview) => <PreviewCard key={preview.domain} preview={preview} />)}
        </div>
      ) : null}
      <div className="styleactions">
        {!previews.length ? (
          <button type="button" className="btn primary" disabled={previewing} onClick={onPreview}>
            {previewing ? 'Drafting three previews, about a minute…' : 'Preview 3 drafts'}
          </button>
        ) : null}
        {previews.length && !isLive ? (
          <button type="button" className="btn primary" disabled={busyStep === 'approve' || !anyPreviewPassed} onClick={() => onApprove(profile.version)}>
            {busyStep === 'approve' ? 'Switching your drafts over…' : 'Use this style for my drafts'}
          </button>
        ) : null}
        {previews.length ? (
          <button type="button" className="stylelink" disabled={previewing} onClick={onPreview}>
            {previewing ? 'Drafting new previews…' : 'Draft new previews'}
          </button>
        ) : null}
      </div>

      {previews.length ? <FeedbackForm onAdd={addRule} busy={busyStep === 'rules'} /> : null}

      <details className="styleoptional">
        <summary>Your rules ({ruleCount})</summary>
        <RuleList title="Always" rules={profile.always} onRemove={(index) => removeRule('always', index)} />
        <RuleList title="Never" rules={profile.never} onRemove={(index) => removeRule('never', index)} />
        <RuleList title="Claims about Cartesia" rules={profile.cartesia_claims} />
        {!previews.length ? <FeedbackForm onAdd={addRule} busy={busyStep === 'rules'} /> : null}
      </details>

      <details className="styleoptional">
        <summary>Structure and length per situation</summary>
        <ul className="stylesituations">
          {STYLE_SITUATIONS.map((situation) => {
            const rules = profile.situations[situation.key];
            if (!rules) return null;
            return (
              <li key={situation.key}>
                <strong>{situationLabel(situation.key)}</strong>{rules.inferred ? ' (inferred)' : ''}: about{' '}
                {rules.target_words} words, at most {rules.max_words}. {rules.structure}
              </li>
            );
          })}
        </ul>
      </details>

      {state.versions.length > 1 ? (
        <details className="styleoptional">
          <summary>All versions</summary>
          <ul className="styleversions">
            {[...state.versions].reverse().map((summary) => (
              <li key={summary.version}>
                Version {summary.version}, {summary.created_at.slice(0, 10)}
                {summary.impersonator ? `, by ${summary.impersonator}` : ''}
                {state.active_version === summary.version ? ' · in use' : ''}
                {state.active_version !== summary.version && summary.approved_at ? (
                  <button type="button" className="stylelink" disabled={busyStep === 'approve'} onClick={() => onApprove(summary.version)}>
                    Go back to this one
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
